import {
  connect,
  disconnect,
  getEffectiveClientId,
  isClientIdProvisioned,
  isConnected,
  onAuthChange,
  setStoredClientId,
} from "../drive-auth";
import { getList, onListChange, remove, type TrackedFile } from "../store";
import { openPicker } from "./picker";

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "agora";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days}d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}

function renderHeader(connected: boolean): string {
  const dot = connected ? "ok" : "off";
  const btnLabel = connected ? "Desconectar" : "Conectar Drive";
  return `
    <header class="topbar">
      <div class="brand">Drive HTML Viewer</div>
      <div class="status">
        <span class="dot ${dot}" aria-hidden="true"></span>
        <span>${connected ? "Conectado" : "Desconectado"}</span>
        <button class="ghost" data-action="${connected ? "disconnect" : "connect"}">${btnLabel}</button>
      </div>
    </header>
  `;
}

function renderEmpty(connected: boolean): string {
  if (!connected) {
    return `
      <div class="empty">
        <p>Conecte sua conta do Google Drive para começar.</p>
      </div>
    `;
  }
  return `
    <div class="empty">
      <p>Nenhum HTML adicionado ainda.</p>
      <p class="hint">Clique em <strong>+ Adicionar HTML</strong> para escolher um arquivo do seu Drive.</p>
    </div>
  `;
}

function renderCard(file: TrackedFile): string {
  return `
    <article class="card" data-id="${file.id}" tabindex="0" role="button" aria-label="Abrir ${escapeHtml(file.name)}">
      <div class="card-body">
        <div class="card-title">${escapeHtml(file.name)}</div>
        <div class="card-meta">atualizado ${timeAgo(file.modifiedTime)}</div>
      </div>
      <button class="card-remove" data-remove="${file.id}" title="Remover da lista" aria-label="Remover ${escapeHtml(file.name)}">×</button>
    </article>
  `;
}

function renderList(list: TrackedFile[], connected: boolean): string {
  if (list.length === 0) return renderEmpty(connected);
  return `<div class="cards">${list.map(renderCard).join("")}</div>`;
}

function renderActions(connected: boolean): string {
  return `
    <div class="actions">
      <button class="primary" data-action="add" ${connected ? "" : "disabled"}>+ Adicionar HTML</button>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function mountListView(root: HTMLElement): () => void {
  let connected = isConnected();
  let list = getList();

  function paint(): void {
    root.innerHTML = `
      ${renderHeader(connected)}
      <main class="list-main">
        ${renderActions(connected)}
        ${renderList(list, connected)}
      </main>
    `;
  }

  async function handleConnect(): Promise<void> {
    try {
      let clientId = getEffectiveClientId();
      if (!clientId && !isClientIdProvisioned()) {
        const pasted = window.prompt(
          "Cole seu Google OAuth Client ID (formato 123-abc.apps.googleusercontent.com):",
        );
        if (!pasted) return;
        setStoredClientId(pasted);
        clientId = pasted;
      }
      await connect(clientId ?? undefined);
    } catch (err) {
      alert(`Erro ao conectar: ${(err as Error).message}`);
    }
  }

  async function handleDisconnect(): Promise<void> {
    try {
      await disconnect();
    } catch (err) {
      console.error(err);
    }
  }

  function handleClick(ev: Event): void {
    const target = ev.target as HTMLElement;
    const removeId = target.getAttribute("data-remove");
    if (removeId) {
      ev.stopPropagation();
      if (confirm("Remover este arquivo da lista? (não apaga do Drive)")) {
        remove(removeId);
      }
      return;
    }
    const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
    if (action === "connect") void handleConnect();
    else if (action === "disconnect") void handleDisconnect();
    else if (action === "add") openPicker();
    else {
      const card = target.closest<HTMLElement>(".card");
      const id = card?.dataset.id;
      if (id) location.hash = `#/view/${id}`;
    }
  }

  function handleKey(ev: KeyboardEvent): void {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    const card = (ev.target as HTMLElement).closest<HTMLElement>(".card");
    const id = card?.dataset.id;
    if (id) {
      ev.preventDefault();
      location.hash = `#/view/${id}`;
    }
  }

  paint();
  root.addEventListener("click", handleClick);
  root.addEventListener("keydown", handleKey);

  const offAuth = onAuthChange((c) => {
    connected = c;
    paint();
  });
  const offList = onListChange((l) => {
    list = l;
    paint();
  });

  return () => {
    root.removeEventListener("click", handleClick);
    root.removeEventListener("keydown", handleKey);
    offAuth();
    offList();
  };
}
